import { UserRelatedData } from '@y42/feature';
import { Y42GitFolders } from '@y42/feature/git';
import { Y42MetadataGitModel } from '@y42/feature/json-schema';
import { DefaultFeatureChildListViewProps, FeatureListViewData } from '@y42/feature/shared/list-view';
import { PopoverFormOutput } from '@y42/feature/shared/popover-form';
import { Access, JobStatus, Y42FeatureType } from '@y42/json-schema';
import { ImpexSettingsModel, ImpexState, ImpexStoreModel, ImpexUIModel } from '@y42/impex';
import { DatasourceTableStoreModel, DatasourceTableUIModel } from '../datasource-table/datasource-table.model';
import { DatasourceCTypeModel, DatasourceTypeModel } from '../datasource-type/datasource-type.model';

export interface DatasourceSettings extends ImpexSettingsModel {}

/**
 * A representation of what is being returned by the IntegrationGitService
 * and essentially written to the Store.
 */
export class DatasourceStoreModel<Settings extends DatasourceSettings = DatasourceSettings>
  extends Y42MetadataGitModel
  implements ImpexStoreModel<Settings>
{
  constructor(model: DatasourceStoreModel<Settings>) {
    super(model);
    this.$name = model.$name;
    this.$path = model.$path;
    this.$level = model.$level;
    this.$children = model.$children || [];

    this.settings = model.settings;
  }

  $name: string; // just the folder name
  $path: string; // the whole path needs to be implemented for synching the akita store to GIT
  $level: number;

  settings: Settings; // coming from the settings.json file from GIT

  $children: Array<DatasourceTableStoreModel['$path']>;
}

export type DatasourceUIModel<StoreModel extends DatasourceStoreModel = DatasourceStoreModel> =
  ImpexUIModel<DatasourceTypeModel> &
    StoreModel &
    Partial<UserRelatedData> & {
      typeIcon: NonNullable<ImpexUIModel['typeIcon']>;
      tables: DatasourceTableUIModel[];
    };

export type DatasourceListViewData = FeatureListViewData<
  DatasourceUIModel & {
    rows: number;
    // needs to be number for ag-grid to be able to properly sort
    last_import_date?: number | null;
    data_test_count: number;
  },
  DatasourceTableUIModel & {
    status: JobStatus | null;
  },
  DefaultFeatureChildListViewProps | 'status'
>;

export type DatasourceState<StoreModel extends DatasourceStoreModel = DatasourceStoreModel> = ImpexState<StoreModel>;

export type DatasourceLike = Y42GitFolders.Integration | Y42GitFolders.SqlModel | Y42GitFolders.UiModel;

export const DatasourceFeatureTypes = [Y42FeatureType.Integration, Y42FeatureType.SqlModel, Y42FeatureType.UiModel];

export type CreateDatasourceParams = PopoverFormOutput<DatasourceCTypeModel> & { access: Access };
